import produce from 'immer';
import { create } from 'zustand';

import type { WritableDraft } from 'immer/dist/internal';
import toNumber from 'lodash/toNumber';
import { GRID_STORE } from 'state/grids/gridState';
import { useScrollLeftStore } from 'state/grids/hooks/useScrollLeftStore';

export interface PendingEditsState {
  pendingEditsToClearOnNextGridRender: string[];
  addPendingEditToClearOnNextRender: (pendingEditsToClear: string) => void;
  processPendingEdits: (pendingEditsToClear: string[]) => void;

  pendingRootNodeCreations: PendingRootNodeCreation[];
  addPendingRootNodeCreation: (
    projectId: string,
    templateNodeId: string,
    eventId: string,
    userId: string,
    resultSetEntryId: string
  ) => void;
  removePendingRootNodeCreation: (eventId: string) => void;
  setPendingRootNodeCreationNodeId: (eventId: string, nodeId: string) => void;

  pendingRelationshipChanges: PendingRelationshipChanged[];
  addPendingRelationshipChange: (projectId: string, parentDataNodeId: string, eventId: string, userId: string) => void;
  removePendingRelationshipChange: (eventId: string) => void;

  // removes all pending edits
  clear: () => void;
}

export interface PendingRootNodeCreation {
  projectId: string;
  templateNodeId: string;
  eventId: string;
  userId: string;
  resultSetEntryId: string;
  // the real data node id, filled in once the RootRowAdded event comes back; used to focus the
  // correct row instead of guessing it's the last row in the matrix
  nodeId?: string;
}

export interface PendingRelationshipChanged {
  projectId: string;
  parentDataNodeId: string;
  eventId: string;
  userId: string;
}

// DEV NOTE: this store calls GRID_STORE and the GRID_STORE calls this store as well
// in case of grid recalculations, we need to let the calculation finish before processing the pending edits
// if these stored were merged into one, achieving the 'wait' would be really difficult
export const PENDING_EDITS_STATE = create<PendingEditsState>((set) => ({
  pendingEditsToClearOnNextGridRender: [],
  addPendingEditToClearOnNextRender: (pendingEditToClear: string) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        draft.pendingEditsToClearOnNextGridRender.push(pendingEditToClear);
      });
      return newState;
    }),

  processPendingEdits: (editsToClear: string[]) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        state.pendingEditsToClearOnNextGridRender
          .filter((x) => editsToClear.find((y) => y === x) !== undefined)
          .forEach((item) => {
            handleClearPendingEdit(state, draft, item);
          });
      });
      return newState;
    }),

  pendingRootNodeCreations: [],
  addPendingRootNodeCreation: (
    projectId: string,
    templateNodeId: string,
    eventId: string,
    userId: string,
    resultSetEntryId: string
  ) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        draft.pendingRootNodeCreations.push({
          projectId: projectId,
          templateNodeId: templateNodeId,
          eventId: eventId,
          userId: userId,
          resultSetEntryId: resultSetEntryId
        } as PendingRootNodeCreation);
      });
      return newState;
    }),
  removePendingRootNodeCreation: (eventId: string) =>
    set((state) => {
      return produce(state, (draft) => {
        draft.pendingRootNodeCreations = state.pendingRootNodeCreations.filter((n) => n.eventId !== eventId);
      });
    }),
  setPendingRootNodeCreationNodeId: (eventId: string, nodeId: string) =>
    set((state) => {
      return produce(state, (draft) => {
        const pendingCreation = draft.pendingRootNodeCreations.find((n) => n.eventId === eventId);
        if (pendingCreation !== undefined) {
          pendingCreation.nodeId = nodeId;
        }
      });
    }),

  pendingRelationshipChanges: [],
  addPendingRelationshipChange: (projectId: string, parentDataNodeId: string, eventId: string, userId: string) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        draft.pendingRelationshipChanges.push({
          projectId,
          parentDataNodeId,
          eventId,
          userId
        } as PendingRelationshipChanged);
      });
      return newState;
    }),
  removePendingRelationshipChange: (eventId: string) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        draft.pendingRelationshipChanges = state.pendingRelationshipChanges.filter((n) => n.eventId !== eventId);
      });
      return newState;
    }),

  clear: () =>
    set((state) => {
      const newState = produce(state, (draft) => {
        draft.pendingRootNodeCreations = [];
        draft.pendingRelationshipChanges = [];
      });
      return newState;
    })
}));

function handleClearPendingEdit(state: PendingEditsState, draft: WritableDraft<PendingEditsState>, item: string) {
  const rootNodeCreationIndex = state.pendingRootNodeCreations.findIndex((x) => x.eventId === item);
  if (rootNodeCreationIndex !== -1) {
    const rootNodeCreationToDelete = { ...state.pendingRootNodeCreations[rootNodeCreationIndex] };

    // focus the row of the actual created node rather than assuming it's the last row in the
    // matrix - the matrix can be sorted/filtered, or another concurrent edit can shift rows,
    // so "last row" doesn't reliably point at the node that was just created
    const createdNode = rootNodeCreationToDelete.nodeId
      ? GRID_STORE.getState().rootNodes[rootNodeCreationToDelete.resultSetEntryId]?.[rootNodeCreationToDelete.nodeId]
      : undefined;
    const rowToFocus =
      createdNode?.rowStart ?? GRID_STORE.getState().nodeMatrixRowCount[rootNodeCreationToDelete.resultSetEntryId] - 1;
    const columnSpecificationByIndexMap =
      GRID_STORE.getState().gridSpecifications[rootNodeCreationToDelete.resultSetEntryId].columnSpecificationByIndexMap;
    const firstNonDisabledColumnKey = Object.keys(columnSpecificationByIndexMap)
      .map((key) => toNumber(key))
      .find((key) => {
        const columnSpecification = columnSpecificationByIndexMap[key];
        return !columnSpecification.isDisabled && columnSpecification.propertyTraversal !== null;
      });

    // put the first non-disabled column of the last row in edit-mode and scroll to that position
    if (firstNonDisabledColumnKey !== undefined) {
      const HORIZONTAL_SCROLL_RESET = 0;
      GRID_STORE.getState().editCell(
        rootNodeCreationToDelete.userId,
        rootNodeCreationToDelete.resultSetEntryId,
        firstNonDisabledColumnKey,
        rowToFocus
      );
      GRID_STORE.getState().selectCell(
        rootNodeCreationToDelete.userId,
        rootNodeCreationToDelete.resultSetEntryId,
        firstNonDisabledColumnKey,
        rowToFocus
      );
      GRID_STORE.getState().saveScrollPosition(
        {
          resultSetId: rootNodeCreationToDelete.resultSetEntryId,
          gridPositionStart: rowToFocus,
          gridPositionEnd: rowToFocus
        },
        true
      );
      useScrollLeftStore.getState().setScrollLeft(rootNodeCreationToDelete.resultSetEntryId, HORIZONTAL_SCROLL_RESET);
    }

    draft.pendingRootNodeCreations.splice(rootNodeCreationIndex, 1);
  }

  const relationshipChangeIndex = state.pendingRelationshipChanges.findIndex((x) => x.eventId === item);
  if (relationshipChangeIndex !== -1) {
    draft.pendingRelationshipChanges.splice(relationshipChangeIndex, 1);
  }
}
